import React, { useState, useCallback, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import FilterModuleDateRange from '@/components/filters/FilterModuleDateRange';
import { getMenuData } from '@core/data/menuData';
import AppCard from '@/components/appcard/AppCard';
import { zoneTrackerService } from '@/services/zoneTrackerService';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { FilterRef } from '@features/statistics/types.ts';
import { useNotifications } from '@/core/hooks/useNotifications';

interface FilterArgs {
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
}

const ZoneTrackerOverview: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<any[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);
    const { notifyError } = useNotifications();

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setLoading(true);
            setSeries([]);

            try {
                const data = await zoneTrackerService.loadOverview(args.deviceId, args.moduleId, args.start, args.end);

                const newSeries = [
                    {
                        name: t('count'),
                        data: data.map((element) => ({
                            x: DateTime.fromISO(element.date).setLocale('en').toISODate(),
                            y: element.count
                        }))
                    }
                ];

                setSeries(newSeries);
                setLastUpdate(DateTime.now());
            } catch (error) {
                console.error('Failed to load zone tracker overview:', error);
                notifyError(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-border-all" style={{ fontSize: '16px' }} />}
                title={`${t('zoneTrackerOverview')}`}
                menus={getMenuData()}
                iconSize="18px"
                lastUpdate={lastUpdate}
                currentRoute={{ path: '/statistics/zonetracker/overview' }}
                t={t}
            />

            <Container maxWidth={false} sx={{ pb: 3 }}>
                <FilterModuleDateRange ref={filterRef} onLoadData={loadData} forType="zoneTracker" />

                {loading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <AppCard
                        heading={t('overview')}
                        icon="border_all"
                        iconClass=""
                        colClasses="flex col-12"
                        customClasses="mb-0"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="bar"
                            series={series}
                            height={550}
                            chartId="zone-tracker-overview"
                            xAxisType="category"
                            dataLabelsEnabled={false}
                            yAxisForceNiceScale={true}
                            tooltipShared={false}
                            tooltipIntersect={true}
                        />
                    </AppCard>
                )}
            </Container>
        </Box>
    );
};

ZoneTrackerOverview.displayName = 'ZoneTrackerOverview';

export default ZoneTrackerOverview;
